import { Box, Button, Stack, Square, Text, Image } from '@chakra-ui/react';
import { useTranslations } from 'next-intl';
import { useAppStore } from '~/store/useAppStore';
import { useState } from 'react';
import { postAPI } from '~/lib/api';
import { useToast } from '~/lib/toasts';
import { useUser } from '~/hooks/useUser';
import { useUsers } from '~/hooks/useUsers';

interface LoanContactBannerProps {
  onAnswer: () => void;
}

export const LoanContactBanner = ({ onAnswer }: LoanContactBannerProps) => {
  const isMobile = useAppStore((state) => state.isMobile);
  const t = useTranslations('Banners.LoanContact');
  const [isLoading, setIsLoading] = useState(false);
  const { showToast } = useToast();
  const { user } = useUser();
  const { updateUser } = useUsers();

  const handleAnswer = async (answer: boolean) => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      const now = new Date().toISOString();
      
      // Update user with answer using RLS-protected client-side update
      const updateData = answer
        ? { loan_contact_accepted_at: now }
        : { loan_contact_denied_at: now };

      const { error: updateError } = await updateUser(updateData);

      if (updateError) {
        console.error('Error saving loan contact banner answer:', updateError);
        showToast(
          {
            title: 'Ett fel uppstod',
            description: 'Kunde inte spara ditt svar. Försök igen.',
          },
          { status: 'error' }
        );
        return;
      }

      // If user answered yes, create a lead in Söderberg
      if (answer) {
        const { error: leadError } = await postAPI(
          '/providers/soderberg/leads',
          JSON.stringify({
            firstName: user.first_name || '',
            lastName: user.last_name || '',
            email: user.email || '',
            phone: user.phone || '',
            userId: user.id,
            sourceSystemIdentifier: `habity_user_${user.id}`,
            category: 'Loan Promise Request (via banner)',
          })
        );

        if (leadError) {
          console.error('Error creating Söderberg lead:', leadError);
          // Still show success - we saved their preference
        }

        showToast(
          {
            title: 'Tack!',
            description: 'Vi kontaktar dig snart angående lånelöfte.',
          },
          { status: 'success' }
        );
      }

      // Always hide banner after answer is saved
      onAnswer();
    } catch (error) {
      console.error('Error handling loan contact banner:', error);
      showToast(
        {
          title: 'Ett fel uppstod',
          description: 'Kunde inte spara ditt svar. Försök igen.',
        },
        { status: 'error' }
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      bg="bg-surface"
      px={{ base: '4', md: '3' }}
      py={{ base: '4', md: '2.5' }}
      position="fixed"
      bottom={{ base: '80px', sm: '80px', md: '20px', lg: '20px' }}
      left="50%"
      boxShadow="lg"
      borderRadius="xl"
      zIndex="modal"
      w={{ base: '90%', sm: '90%', md: '75%', lg: '60%' }}
      transform="translateX(-50%)"
    >
      <Stack
        direction={{ base: 'column', sm: 'row' }}
        justify="space-between"
        spacing={{ base: '3', md: '2' }}
        pb="0.5"
      >
        <Stack
          spacing="4"
          direction={{ base: 'column', md: 'row' }}
          align={{ base: 'start', md: 'center' }}
        >
          {!isMobile && (
            <Square size="12" bg="bg-subtle" borderRadius="md">
              <Image 
                src="/eo/logos/soderberg.png" 
                alt="Söderberg & Partners" 
                width="48px"
                height="48px"
                objectFit="contain"
              />
            </Square>
          )}
          <Stack spacing="0.5" pe={{ base: '0', sm: '4' }}>
            <Text fontWeight="medium" fontSize="sm">
              {t('title')}
            </Text>
            <Text color="muted" fontSize="sm">
              {t('description')}
            </Text>
          </Stack>
        </Stack>
        <Stack
          direction={{ base: 'column', sm: 'row' }}
          spacing={{ base: '3', sm: '2' }}
          align={{ base: 'stretch', sm: 'center' }}
        >
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleAnswer(false)}
            isDisabled={isLoading}
          >
            {t('no')}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => handleAnswer(true)}
            isLoading={isLoading}
          >
            {t('yes')}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};
